import {useEffect, useSyncExternalStore} from "react";
import store from "@/store";
import {ScoutingReportsAPI} from "@/api/scouting_reports";
import {
	getDsContext,
	isDsContextReady,
	onDsContextChange,
} from "@/utils/dsContext";

const empty = () => ({
	reports: [],
	templates: [],
	details: {},
	detailErrors: {},
	status: "idle",
	error: null,
	busy: false,
});
let state = empty();
let generation = 0;
let pending = null;
const detailsPending = new Map();
const listeners = new Set();
const publish = (changes) => {
	state = {...state, ...changes};
	listeners.forEach((listener) => listener());
};
onDsContextChange(() => {
	generation++;
	pending = null;
	detailsPending.clear();
	publish(empty());
});
const access = () => {
	const user = store.getState().user;
	return (
		user?.roleName?.toLowerCase() === "admin" ||
		user?.areaPermissions?.some(
			(p) => p.area === "Scouting" && p.accessLevel === "FullControl",
		)
	);
};
export const reportData = () => {
	getDsContext();
	return state;
};
export const refreshReports = () => {
	const context = getDsContext();
	if (!isDsContextReady())
		return Promise.reject(new Error("Sessione scouting non disponibile"));
	if (pending) return pending;
	const ticket = ++generation;
	publish({status: "loading", error: null});
	const request = Promise.all([
		ScoutingReportsAPI.list(store.getState().season.id),
		ScoutingReportsAPI.templates(),
	])
		.then(([reports, templates]) => {
			if (context !== getDsContext() || ticket !== generation)
				throw new Error("Richiesta superata");
			const details = {...state.details};
			const seasonId = store.getState().season.id;
			Object.entries(details).forEach(([id, report]) => {
				if (report?.seasonId === seasonId)
					details[id] = reports.find((row) => row.id === id) || null;
			});
			publish({reports, templates, details, status: "ready", error: null});
		})
		.catch((error) => {
			if (context === getDsContext() && ticket === generation)
				publish({
					reports: [],
					templates: [],
					details: {},
					status: "error",
					error,
				});
			throw error;
		})
		.finally(() => {
			if (pending === request) pending = null;
		});
	pending = request;
	return request;
};
export const fetchReport = (id) => {
	const context = getDsContext();
	if (!isDsContextReady())
		return Promise.reject(new Error("Sessione scouting non disponibile"));
	if (detailsPending.has(id)) return detailsPending.get(id);
	const request = ScoutingReportsAPI.get(id)
		.then((report) => {
			if (context !== getDsContext())
				throw new Error("Sessione scouting cambiata");
			publish({
				details: {...state.details, [id]: report},
				detailErrors: {...state.detailErrors, [id]: null},
			});
			return report;
		})
		.catch((error) => {
			if (context === getDsContext())
				publish({
					details: {...state.details, [id]: null},
					detailErrors: {...state.detailErrors, [id]: error},
				});
			throw error;
		})
		.finally(() => {
			if (detailsPending.get(id) === request) detailsPending.delete(id);
		});
	detailsPending.set(id, request);
	return request;
};
const mutate = async (operation, apply) => {
	const context = getDsContext();
	if (!access() || state.busy || state.status !== "ready")
		throw new Error("Attendi il caricamento o verifica i permessi scouting");
	publish({busy: true});
	generation++;
	pending = null;
	try {
		const result = await operation();
		if (context !== getDsContext())
			throw new Error("Sessione scouting cambiata");
		apply(result);
		return result;
	} finally {
		if (context === getDsContext()) publish({busy: false});
	}
};
const sorted = (rows) =>
	[...rows].sort(
		(a, b) =>
			b.reportDate.localeCompare(a.reportDate) ||
			b.creationDate.localeCompare(a.creationDate) ||
			a.id.localeCompare(b.id),
	);
export const saveReport = (draft) =>
	mutate(
		() =>
			ScoutingReportsAPI.save({
				id: draft.id,
				playerId: draft.playerId,
				templateId: draft.templateId,
				seasonId: draft.seasonId || store.getState().season.id,
				matchId: draft.matchId || null,
				visionId: draft.visionId || null,
				reportDate: draft.reportDate,
				verdict: draft.verdict,
				judgment: draft.judgment,
				values: draft.values || {},
				version: draft.version,
			}),
		(saved) =>
			publish({
				reports:
					saved.seasonId === store.getState().season.id
						? sorted([...state.reports.filter((r) => r.id !== saved.id), saved])
						: state.reports,
				details: {...state.details, [saved.id]: saved},
			}),
	);
export const deleteReport = (id, version) =>
	mutate(
		() => ScoutingReportsAPI.remove(id, version),
		() =>
			publish({
				reports: state.reports.filter((r) => r.id !== id),
				details: {...state.details, [id]: null},
			}),
	);
export const saveTemplate = (draft) =>
	mutate(
		() => ScoutingReportsAPI.saveTemplate(draft),
		(saved) =>
			publish({
				templates: [...state.templates.filter((t) => t.id !== saved.id), saved],
			}),
	);
export const removeTemplate = (id, version) =>
	mutate(
		() => ScoutingReportsAPI.removeTemplate(id, version),
		(saved) =>
			publish({
				templates: state.templates.map((t) => (t.id === id ? saved : t)),
			}),
	);
export const createDefaultTemplates = () =>
	mutate(
		() => ScoutingReportsAPI.defaults(),
		(templates) => publish({templates}),
	);
export const reportError = (error) => {
	const message =
		error?.response?.data?.error || error?.response?.data?.message;
	return typeof message === "string"
		? message
		: "Operazione non riuscita. Ricarica e riprova.";
};
export const useScoutingReports = ({reportId} = {}) => {
	const context = getDsContext();
	const current = useSyncExternalStore(
		(listener) => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},
		() => state,
	);
	useEffect(() => {
		if (!isDsContextReady()) return;
		const reload = () => {
			if (!document.hidden && !state.busy) refreshReports().catch(() => {});
		};
		reload();
		window.addEventListener("focus", reload);
		const timer = setInterval(reload, 30000);
		return () => {
			window.removeEventListener("focus", reload);
			clearInterval(timer);
		};
	}, [context]);
	useEffect(() => {
		if (reportId && isDsContextReady()) fetchReport(reportId).catch(() => {});
	}, [context, reportId]);
	const detailError = reportId ? current.detailErrors[reportId] : null;
	return {
		...current,
		report: reportId ? current.details[reportId] || null : null,
		detailError,
		loading: current.status === "idle" || current.status === "loading",
		detailLoading: !!reportId && !(reportId in current.details) && !detailError,
		canWrite: !!access() && current.status === "ready" && !current.busy,
		refresh: refreshReports,
	};
};
